import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { getFirestore, collection, addDoc, serverTimestamp, setDoc, doc } from 'firebase/firestore';
import { firebaseApp } from '../firebase';
import Globe from '../components/Globe';
import { useLanguage } from '../contexts/LanguageContext';
import { useSignupModal } from '../contexts/SignupModalContext';
import { translations } from '../translations';
import '../styles/Globe.css';

const HomePage = () => {
  const { showSignupModal, openSignupModal, closeSignupModal } = useSignupModal();
  const [activeTab, setActiveTab] = useState('individualSearch');
  const { language } = useLanguage();
  const t = translations[language];
  const [annual, setAnnual] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null); // 'success', 'error', or null
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  
  // Swipe functionality state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Tab order for swipe navigation
  const tabOrder = ['individualSearch', 'caseManagement', 'automatedMonitoring', 'massUpload'];

  // Initialize Firestore
  const db = getFirestore(firebaseApp);

  // Function to handle newsletter subscription
  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !/\S+@\S+\.\S+/.test(newsletterEmail)) {
      setNewsletterStatus('error');
      return;
    }
    setNewsletterLoading(true);
    setNewsletterStatus(null);
    try {
      await setDoc(
        doc(db, 'newsletter-emails', newsletterEmail),
        {
          email: newsletterEmail,
          createdAt: serverTimestamp(),
        }
      );
      setNewsletterStatus('success');
      setNewsletterEmail('');
    } catch (error) {
      console.error('Error adding document: ', error);
      setNewsletterStatus('error');
    }
    setNewsletterLoading(false);
  };
  
  // Function to handle "Start Free Trial" button clicks
  const handleStartFreeTrial = (e) => {
    e.preventDefault();
    openSignupModal();
  };
  
  // Swipe functionality handlers
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe || isRightSwipe) {
      const currentIndex = tabOrder.indexOf(activeTab);
      let newIndex;

      if (isLeftSwipe) {
        // Swipe left - go to next tab
        newIndex = Math.min(currentIndex + 1, tabOrder.length - 1);
      } else {
        // Swipe right - go to previous tab
        newIndex = Math.max(currentIndex - 1, 0);
      }

      setActiveTab(tabOrder[newIndex]);
    }
  };
  
  // Set document title
  useEffect(() => {
    document.title = 'Sanction Screener';
  }, []);

  // Add effect to scroll to the section based on URL hash
  useEffect(() => {
    // Get the hash from the URL (e.g., #features)
    const hash = window.location.hash;
    // If there's a hash, find the element and scroll to it
    if (hash) {
      // Add a small delay to ensure the page is fully rendered
      const scrollToElement = () => {
        const element = document.querySelector(hash);
        if (element) {
          // Get the header height to account for the sticky header
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 0;
          
          // Calculate the target scroll position
          const elementPosition = element.offsetTop;
          const targetPosition = elementPosition - headerHeight;
          
          // Smooth scroll to the target position
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      };
      
      // Try immediately, then with a small delay to ensure DOM is ready
      scrollToElement();
      setTimeout(scrollToElement, 100);
    }
  }, []);

  const massScreeningResults = [
    { name: 'John Smith', verificationId: 'SCR-250628-JNC16B-8812', matches: 0 },
    { name: 'Jane Smith', verificationId: 'SCR-250628-BAAAD6-7066', matches: 2 },
    { name: 'Michael Johnson', verificationId: 'SCR-250628-B888E8-7728', matches: 1 },
    { name: 'Emily Davis', verificationId: 'SCR-250628-ECDE06-1817', matches: 0 },
    { name: 'Robert Brown', verificationId: 'SCR-250628-5B9547-7032', matches: 0 },
    { name: 'Sarah Wilson', verificationId: 'SCR-250628-D1365A-8685', matches: 0 },
    { name: 'David Miller', verificationId: 'SCR-250628-93F4B1-7735', matches: 0 },
    { name: 'Laura Taylor', verificationId: 'SCR-250628-42A08A-2854', matches: 0 },
    { name: 'James Anderson', verificationId: 'SCR-250628-CB07A8-4240', matches: 0 },
    { name: 'Jessica Thomas', verificationId: 'SCR-250628-5534FC-1110', matches: 0 },
  ];

  // FAQ data structure
  const faq = [
    ...(translations[language]?.contactPage?.faq || [
      {
        question: 'What are your support hours?',
        answer: 'Our customer support team is available Monday through Friday, from 9:00 AM to 6:00 PM Central European Time. Enterprise customers have access to extended support hours.'
      },
      {
        question: 'How quickly can I expect a response?',
        answer: 'For general inquiries, we strive to respond within 24 hours during business days. Technical support tickets are prioritized based on severity and your service level, with response times ranging from 1 hour to 24 hours.'
      },
      {
        question: 'Do you offer on-site training and implementation?',
        answer: 'Yes, we offer on-site training and implementation services for Enterprise customers throughout Europe. Please contact our sales team to discuss your specific requirements and arrange a customized solution.'
      },
      {
        question: 'How do I upgrade my subscription plan?',
        answer: 'You can upgrade your subscription plan at any time through your account dashboard. If you need assistance, please contact our customer support team, and they will guide you through the process.'
      }
    ]),
    // Add FAQ items from PricingPage.js
    ...(translations[language]?.pricingPage?.faq ? [
      {
        question: translations[language].pricingPage.faq.trialEnd.question,
        answer: translations[language].pricingPage.faq.trialEnd.answer
      },
      {
        question: translations[language].pricingPage.faq.paymentMethods.question,
        answer: translations[language].pricingPage.faq.paymentMethods.answer
      },
      {
        question: translations[language].pricingPage.faq.changePlans.question,
        answer: translations[language].pricingPage.faq.changePlans.answer
      },
      {
        question: translations[language].pricingPage.faq.exceedLimit.question,
        answer: translations[language].pricingPage.faq.exceedLimit.answer
      },
      {
        question: translations[language].pricingPage.faq.nonProfit.question,
        answer: translations[language].pricingPage.faq.nonProfit.answer
      },
      {
        question: translations[language].pricingPage.faq.contractPeriod.question,
        answer: translations[language].pricingPage.faq.contractPeriod.answer
      }
    ] : [])
  ];

  return (
    <div className="font-sans">
      {/* Custom CSS for gradient and swipe functionality */}
      <style>{`
        .gradient-wrapper {
          background: linear-gradient(to bottom, white 0%, white 40%, #dbeafe 70%,rgb(169, 191, 219) 100%);
          position: relative;
          overflow: hidden;
        }
        

        
        /* Smooth transitions for tab content */
        .tab-content {
          transition: opacity 0.3s ease;
        }
        
        /* Prevent text selection during swipe */
        .swipe-container {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
      `}</style>
      
      {/* Hero Section - New, Centered, No Illustration */}
      <section id="home" className="bg-gradient-to-br from-indigo-950 via-blue-900 to-indigo-900 text-white overflow-hidden relative py-32 min-h-[600px]">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:items-center">
            {/* Left Column - Text Content */}
            <div className="lg:w-[60%] text-center lg:text-left">
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                {t.heroTitle}
              </h1>
              <p className="text-xl md:text-2xl mb-10 text-blue-200">
                {t.heroSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center lg:justify-start mb-6">
                <a 
                  href="#" 
                  onClick={handleStartFreeTrial}
                  className="bg-white text-indigo-900 hover:bg-indigo-50 font-semibold py-3 px-8 rounded-md text-center transition duration-200"
                >
                  {t.startFreeTrial}
                </a>
              </div>
              {/* Compliance Badges */}
              <div className="flex flex-wrap justify-center lg:justify-start items-center gap-6 mt-4 text-sm">
                <div className="flex items-center gap-2 text-amber-300"><span role="img" aria-label="GDPR">🔒</span> {t.gdprCompliant}</div>
                <div className="flex items-center gap-2 text-sky-300"><span role="img" aria-label="Watchlists">🌐</span> {t.globalWatchlists}</div>
                <div className="flex items-center gap-2 text-orange-300"><span role="img" aria-label="Sub-second">⚡</span> {t.superfastResults}</div>
                <div className="flex items-center gap-2 text-indigo-200"><span role="img" aria-label="Audit">📋</span> {t.auditReady}</div>
              </div>
            </div>
            {/* Right Column - Globe */}
            <div className="lg:w-[40%] flex justify-center items-center">
              <div className="max-w-[500px] w-full h-auto">
                <Globe />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges & Solution Section */}
      <section className="py-20 px-5 bg-gradient-to-b from-slate-50 to-slate-200">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
            {t.challengesTitle}
          </h2>
          <p className="text-center text-lg text-gray-600 mb-16 max-w-2xl mx-auto">
            {t.challengesSubtitle}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 items-center max-w-6xl mx-auto">
            {/* Before Sanction Screener Card */}
            <div className="bg-white rounded-2xl shadow-lg border-t-4 border-red-500 p-6 md:mr-[-30px]">
              <div className="flex items-center gap-3 mb-6">
                <h3 className="text-xl font-bold text-red-500">{t.beforeProtego}</h3>
              </div>
              <ul className="space-y-4">
                {t.beforeProtegoPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-500 text-sm">✕</span>
                    <span className="text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* VS Divider */}
            <div className="hidden md:flex flex-col items-center justify-center z-10">
              <div className="w-0.5 h-full bg-gray-200 relative">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="w-12 h-12 rounded-full bg-blue-900 flex items-center justify-center text-white font-bold text-sm">
                    VS
                  </div>
                </div>
              </div>
            </div>

            {/* After Sanction Screener Card */}
            <div className="bg-white rounded-2xl shadow-lg border-t-4 border-emerald-500 p-6 md:ml-[-30px]">
              <div className="flex items-center gap-3 mb-6">
                <h3 className="text-xl font-bold text-emerald-500">{t.afterProtego}</h3>
              </div>
              <ul className="space-y-4">
                {t.afterProtegoPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-500 text-sm">✓</span>
                    <span className="text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Showcase Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-950 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-6 text-white">{t.featuresShowcaseTitle}</h2>
            <p className="text-xl text-blue-200">
              {t.featuresShowcaseSubtitle}
            </p>
          </div>

          {/* Tab Navigation - Improved Mobile UX */}
          <div className="mb-12">
            {/* Mobile: Vertical stacked tabs with better touch targets */}
            <div className="md:hidden space-y-2 mb-8">
              {[
                { key: 'individualSearch', label: t.individualSearch, icon: '🔍' },
                { key: 'caseManagement', label: t.caseManagement, icon: '📋' },
                { key: 'automatedMonitoring', label: t.automatedMonitoring, icon: '🔄' },
                { key: 'massUpload', label: t.massUpload, icon: '📤' }
              ].map((tab) => (
                <button 
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                    activeTab === tab.key 
                      ? 'border-blue-400 bg-blue-900/30 text-white shadow-lg' 
                      : 'border-blue-800/30 text-blue-200 hover:border-blue-600/50 hover:bg-blue-900/20'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{tab.icon}</span>
                    <span className="font-medium text-base">{tab.label}</span>
                    {activeTab === tab.key && (
                      <svg className="w-5 h-5 ml-auto text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Desktop: Horizontal tabs */}
            <div className="hidden md:flex justify-center border-b border-blue-800/50 gap-2 md:gap-0 md:overflow-x-auto md:scrollbar-thin md:scrollbar-thumb-blue-800/30 md:scrollbar-track-transparent md:-mx-4 md:px-4">
              <button 
                onClick={() => setActiveTab('individualSearch')}
                className={`min-w-max px-4 sm:px-8 py-2 sm:py-4 text-sm sm:text-base text-blue-200 hover:text-white border-b-2 font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'individualSearch' ? 'border-blue-500 text-white' : 'border-transparent'
                }`}
              >
                {t.individualSearch}
              </button>
              <button 
                onClick={() => setActiveTab('caseManagement')}
                className={`min-w-max px-4 sm:px-8 py-2 sm:py-4 text-sm sm:text-base text-blue-200 hover:text-white border-b-2 font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'caseManagement' ? 'border-blue-500 text-white' : 'border-transparent'
                }`}
              >
                {t.caseManagement}
              </button>
              <button 
                onClick={() => setActiveTab('automatedMonitoring')}
                className={`min-w-max px-4 sm:px-8 py-2 sm:py-4 text-sm sm:text-base text-blue-200 hover:text-white border-b-2 font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'automatedMonitoring' ? 'border-blue-500 text-white' : 'border-transparent'
                }`}
              >
                {t.automatedMonitoring}
              </button>
              <button 
                onClick={() => setActiveTab('massUpload')}
                className={`min-w-max px-4 sm:px-8 py-2 sm:py-4 text-sm sm:text-base text-blue-200 hover:text-white border-b-2 font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'massUpload' ? 'border-blue-500 text-white' : 'border-transparent'
                }`}
              >
                {t.massUpload}
              </button>
            </div>
          </div>

          {/* Tab Content - Responsive: side-by-side on desktop, stacked on mobile */}
          <div 
            className="max-w-6xl mx-auto relative swipe-container space-y-8 md:grid md:grid-cols-2 md:gap-16 md:space-y-0"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{ touchAction: 'pan-y' }} // Allow vertical scrolling while enabling horizontal swipe
          >
            {/* Text Content - Left on desktop, top on mobile */}
            <div>
              {activeTab === 'individualSearch' && (
                <>
                  <h3 className="text-2xl lg:text-3xl font-bold mb-4 lg:mb-6">{t.individualSearchTitle}</h3>
                  <p className="text-base lg:text-lg text-blue-200 mb-6 lg:mb-8">
                    {t.individualSearchDesc}
                  </p>
                  <ul className="space-y-3 lg:space-y-4 mb-6 lg:mb-8">
                    {t.individualSearchPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs lg:text-sm mt-0.5">✓</span>
                        <span className="text-blue-100 text-sm lg:text-base">{point}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {activeTab === 'caseManagement' && (
                <>
                  <h3 className="text-2xl lg:text-3xl font-bold mb-4 lg:mb-6">{t.caseManagementTitle}</h3>
                  <p className="text-base lg:text-lg text-blue-200 mb-6 lg:mb-8">
                    {t.caseManagementDesc}
                  </p>
                  <ul className="space-y-3 lg:space-y-4 mb-6 lg:mb-8">
                    {t.caseManagementPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs lg:text-sm mt-0.5">✓</span>
                        <span className="text-blue-100 text-sm lg:text-base">{point}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {activeTab === 'automatedMonitoring' && (
                <>
                  <h3 className="text-2xl lg:text-3xl font-bold mb-4 lg:mb-6">{t.automatedMonitoringTitle}</h3>
                  <p className="text-base lg:text-lg text-blue-200 mb-6 lg:mb-8">
                    {t.automatedMonitoringDesc}
                  </p>
                  <ul className="space-y-3 lg:space-y-4 mb-6 lg:mb-8">
                    {t.automatedMonitoringPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs lg:text-sm mt-0.5">✓</span>
                        <span className="text-blue-100 text-sm lg:text-base">{point}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {activeTab === 'massUpload' && (
                <>
                  <h3 className="text-2xl lg:text-3xl font-bold mb-4 lg:mb-6">{t.massUploadTitle.replace('Mass Upload', 'Bulk Upload')}</h3>
                  <p className="text-base lg:text-lg text-blue-200 mb-6 lg:mb-8">
                    {t.massUploadDesc}
                  </p>
                  <ul className="space-y-3 lg:space-y-4 mb-6 lg:mb-8">
                    {t.massUploadPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs lg:text-sm mt-0.5">✓</span>
                        <span className="text-blue-100 text-sm lg:text-base">{point}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            {/* Visual Mockup - Right on desktop, below text on mobile */}
            <div>
              {activeTab === 'individualSearch' && (
                <div className="bg-indigo-950/50 rounded-xl border border-indigo-800/50 overflow-hidden shadow-2xl max-w-full mt-2 md:mt-0">
                  {/* Header with search input */}
                  <div className="bg-indigo-900/50 px-4 lg:px-6 py-3 lg:py-4 border-b border-indigo-800/50">
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-stretch sm:items-center">
                      <input 
                        type="text" 
                        className="flex-grow bg-indigo-900/50 border-2 border-indigo-800/50 rounded-lg px-3 lg:px-4 py-2 text-white placeholder-blue-300/50 focus:border-blue-500 focus:outline-none transition-colors text-sm lg:text-base" 
                        value="John Smith" 
                        readOnly 
                        placeholder={t.searchPlaceholder}
                      />
                      <button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 lg:px-6 py-2 rounded-lg font-medium text-sm lg:text-base whitespace-nowrap">{t.search}</button>
                    </div>
                  </div>
                  {/* Search Results Body */}
                  <div className="p-4 lg:p-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
                      <h3 className="font-bold text-white text-base lg:text-lg">{t.individualSearchResults}</h3>
                      <div className="text-xs lg:text-sm text-blue-300">{t.foundMatches.replace('{count}', 2).replace('{name}', 'John Smith')}</div>
                    </div>
                    {/* Risk Level Filters */}
                    <div className="flex flex-wrap gap-2 mb-4 lg:mb-6">
                      <span className="bg-red-500/20 text-red-400 px-2 lg:px-3 py-1 rounded-full text-xs font-semibold">{t.veryHigh} (2)</span>
                      <span className="bg-gray-500/20 text-gray-400 px-2 lg:px-3 py-1 rounded-full text-xs font-semibold">{t.low} (0)</span>
                    </div>
                    {/* Result 1 */}
                    <div className="bg-indigo-900/30 rounded-xl p-3 lg:p-4 mb-3 lg:mb-4 border border-indigo-800/50">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                        <div className="flex-1">
                          <h4 className="font-bold text-white text-base lg:text-lg">Johnathan Smith</h4>
                          <div className="flex flex-wrap gap-1 lg:gap-2 mt-1">
                            <span className="bg-red-500/80 text-white text-xs font-bold px-2 py-1 rounded-md">{t.pep}</span>
                            <span className="bg-blue-500/80 text-white text-xs font-bold px-2 py-1 rounded-md">{t.sanctions}</span>
                          </div>
                        </div>
                        <div className="text-right sm:text-left">
                          <div className="text-xl lg:text-2xl font-bold text-red-400">98%</div>
                          <div className="text-xs text-red-300">{t.veryHighRisk}</div>
                        </div>
                      </div>
                      <div className="border-t border-indigo-800/50 my-2 lg:my-3"></div>
                      <div>
                        <h5 className="text-xs lg:text-sm font-semibold text-blue-200 mb-1 lg:mb-2">{t.alternativeNames}</h5>
                        <ul className="text-xs lg:text-sm text-blue-300 space-y-1">
                          <li>Jon Smith</li>
                          <li>Johnny Smith</li>
                        </ul>
                      </div>
                    </div>
                    {/* Result 2 */}
                    <div className="bg-indigo-900/30 rounded-xl p-3 lg:p-4 mb-3 lg:mb-4 border border-indigo-800/50">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                        <div className="flex-1">
                          <h4 className="font-bold text-white text-base lg:text-lg">Global Smith Trading</h4>
                          <div className="flex flex-wrap gap-1 lg:gap-2 mt-1">
                            <span className="bg-red-500/80 text-white text-xs font-bold px-2 py-1 rounded-md">{t.sanctions}</span>
                          </div>
                        </div>
                        <div className="text-right sm:text-left">
                          <div className="text-xl lg:text-2xl font-bold text-red-400">95%</div>
                          <div className="text-xs text-red-300">{t.veryHighRisk}</div>
                        </div>
                      </div>
                      <div className="border-t border-indigo-800/50 my-2 lg:my-3"></div>
                      <div>
                        <h5 className="text-xs lg:text-sm font-semibold text-blue-200 mb-1 lg:mb-2">{t.knownAssociates}</h5>
                        <ul className="text-xs lg:text-sm text-blue-300 space-y-1">
                          <li>John Smith</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'caseManagement' && (
                <div className="bg-indigo-950/50 rounded-xl border border-indigo-800/50 overflow-hidden shadow-2xl p-4 lg:p-6 max-w-full mt-2 md:mt-0">
                  <div className="flex justify-between items-center mb-4">
                    <div className="font-medium text-white text-sm lg:text-base">{t.caseManagementTitle}</div>
                    <button className="bg-blue-800 text-white px-2 lg:px-3 py-1 rounded text-xs font-semibold">{t.refresh}</button>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4 mb-4 lg:mb-6">
                    <div className="bg-indigo-900/30 rounded-xl p-3 lg:p-4 text-center flex flex-col items-center justify-center">
                      <div className="text-xl lg:text-2xl font-bold text-white leading-none">8</div>
                      <div className="text-blue-200 text-xs mt-1 break-words">{t.totalCases}</div>
                    </div>
                    <div className="bg-red-600/80 rounded-xl p-3 lg:p-4 text-center flex flex-col items-center justify-center">
                      <div className="text-xl lg:text-2xl font-bold text-white leading-none">3</div>
                      <div className="text-red-100 text-xs mt-1 break-words">{t.open}</div>
                    </div>
                    <div className="bg-purple-600/80 rounded-xl p-3 lg:p-4 text-center flex flex-col items-center justify-center">
                      <div className="text-xl lg:text-2xl font-bold text-white leading-none">3</div>
                      <div className="text-purple-100 text-xs mt-1 break-words">{t.inProgress}</div>
                    </div>
                    <div className="bg-emerald-600/80 rounded-xl p-3 lg:p-4 text-center flex flex-col items-center justify-center">
                      <div className="text-xl lg:text-2xl font-bold text-white leading-none">2</div>
                      <div className="text-emerald-100 text-xs mt-1 break-words">{t.completed}</div>
                    </div>
                  </div>
                  <div className="bg-indigo-900/30 rounded-xl p-3 lg:p-4 mb-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-2">
                      <div className="font-semibold text-white text-sm lg:text-base">Alexander Johnson</div>
                      <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded self-start sm:self-auto">{t.inProgress}</span>
                    </div>
                    <div className="text-blue-200 text-xs mb-2">{t.matches}: 4 • {t.highRisk}</div>
                    <div className="w-full bg-blue-900/40 rounded-full h-2">
                      <div className="bg-purple-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                    </div>
                    <div className="text-right text-xs text-blue-100 mt-1">3 {t.of} 4 {t.matchesReviewed}</div>
                  </div>
                  <div className="bg-indigo-900/30 rounded-xl p-3 lg:p-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-2">
                      <div className="font-semibold text-white text-sm lg:text-base">Global Industries Ltd</div>
                      <span className="bg-emerald-600 text-white text-xs px-2 py-1 rounded self-start sm:self-auto">{t.completed}</span>
                    </div>
                    <div className="text-blue-200 text-xs mb-2">{t.matches}: 2 • {t.highRisk}</div>
                    <div className="w-full bg-blue-900/40 rounded-full h-2">
                      <div className="bg-emerald-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                    <div className="text-right text-xs text-blue-100 mt-1">2 {t.of} 2 {t.matchesReviewed}</div>
                  </div>
                </div>
              )}
              {activeTab === 'automatedMonitoring' && (
                <div className="bg-indigo-950/50 rounded-xl border border-indigo-800/50 overflow-hidden shadow-2xl p-4 lg:p-6 max-w-full mt-2 md:mt-0">
                  <div className="font-medium text-white mb-4 text-sm lg:text-base">{t.monitoring}</div>
                  <div className="mb-4 lg:mb-6">
                    <label className="block text-blue-200 text-xs mb-2">{t.automaticRescreeningFrequency}</label>
                    <select className="w-full bg-indigo-900/50 border border-indigo-800/50 rounded-lg px-3 lg:px-4 py-2 text-white focus:border-blue-500 focus:outline-none transition-colors text-sm">
                      <option>{t.every6Months}</option>
                      <option>{t.everyMonth}</option>
                      <option>{t.everyWeek}</option>
                      <option>{t.everyDay}</option>
                    </select>
                  </div>
                  <div className="text-blue-200 text-xs mb-3">{t.totalMonitored}: <span className="font-bold text-white">4</span></div>
                  <div className="bg-indigo-900/30 rounded-xl p-3 lg:p-4 mb-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex-1">
                      <div className="font-semibold text-white text-sm lg:text-base">Sarah Mitchell</div>
                      <div className="text-blue-200 text-xs">ID: SCR-160625-B0E73-3682</div>
                      <div className="text-blue-200 text-xs">{t.checked}: 6/16/2025, 5:37:05</div>
                    </div>
                    <div className="flex gap-2 self-start sm:self-auto">
                      <button className="bg-blue-600 text-white px-2 lg:px-3 py-1 rounded text-xs font-semibold">{t.run}</button>
                      <button className="bg-red-600 text-white px-2 lg:px-3 py-1 rounded text-xs font-semibold">{t.remove}</button>
                    </div>
                  </div>
                  <div className="bg-indigo-900/30 rounded-xl p-3 lg:p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex-1">
                      <div className="font-semibold text-white text-sm lg:text-base">International Corp</div>
                      <div className="text-blue-200 text-xs">ID: SCR-160625-476C7F-5521</div>
                      <div className="text-blue-200 text-xs">{t.checked}: 6/16/2025, 5:37:05</div>
                    </div>
                    <div className="flex gap-2 self-start sm:self-auto">
                      <button className="bg-blue-600 text-white px-2 lg:px-3 py-1 rounded text-xs font-semibold">{t.run}</button>
                      <button className="bg-red-600 text-white px-2 lg:px-3 py-1 rounded text-xs font-semibold">{t.remove}</button>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'massUpload' && (
                <div className="bg-indigo-950/50 rounded-xl border border-indigo-800/50 overflow-hidden shadow-2xl max-w-full mt-2 md:mt-0">
                  {/* Header */}
                  <div className="p-4 lg:p-6 border-b border-indigo-800/50">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-2">
                      <h3 className="font-bold text-white text-lg lg:text-xl">{t.bulkScreening}</h3>
                      <button className="bg-blue-800/80 hover:bg-blue-800 text-white px-3 lg:px-4 py-1.5 rounded-md text-xs font-semibold transition-colors self-start sm:self-auto">{t.clearAndNew}</button>
                    </div>
                    <div className="text-xs lg:text-sm text-blue-300">
                      <span>&larr; {t.backToJobs}</span>
                      <span className="mx-2 text-blue-600">|</span>
                      <span>{t.currentJob}: <span className="font-mono text-blue-200 text-xs">f47j3hds61imQFftzqGdiF</span></span>
                    </div>
                  </div>
                  {/* Overall Progress */}
                  <div className="bg-indigo-900/30 p-4 lg:p-6 border-b border-indigo-800/50">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
                      {/* Left: Status Block */}
                      <div className="flex-1 flex items-center lg:border-r lg:pr-8">
                        <div className="flex flex-col w-full">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center bg-emerald-500/10 rounded-full text-emerald-400">
                              <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                            </div>
                            <span className="font-bold text-white text-base lg:text-lg">{t.completed}</span>
                          </div>
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-blue-200 text-xs lg:text-sm">{t.status}:</span>
                            <span className="text-blue-200 text-xs lg:text-sm">10 / 10 (100%)</span>
                          </div>
                          <div className="w-full bg-blue-900/40 rounded-full h-2 mt-2">
                            <div className="bg-emerald-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                          </div>
                        </div>
                      </div>
                      {/* Right: Match Summary */}
                      <div className="flex-1 flex flex-col items-center lg:items-start pt-4 lg:pt-0 lg:pl-8 border-t border-indigo-800/50 lg:border-t-0">
                        <div className="text-blue-200 text-xs lg:text-sm space-y-1 text-center lg:text-left">
                          <div>{t.matches}: <span className="font-bold text-white">3</span></div>
                          <div>{t.highConfidenceMatches}: <span className="font-bold text-white">1</span></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Results Table */}
                  <div className="px-2 py-2 lg:px-6 lg:py-4">
                    <div className="bg-indigo-900/30 rounded-lg border border-indigo-800/50 overflow-x-auto">
                      {/* Table Header */}
                      <div className="grid grid-cols-[minmax(0,_2fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_2fr)_minmax(0,_1fr)] gap-2 lg:gap-4 px-2 lg:px-4 py-2 text-xs lg:text-sm font-bold text-blue-200 border-b border-indigo-800/50 min-w-[480px]">
                        <div>{t.name}</div>
                        <div>{t.status}</div>
                        <div>{t.matches}</div>
                        <div>{t.verificationId}</div>
                        <div className="text-right">{t.actions}</div>
                      </div>
                      {/* Table Body */}
                      <div className="text-xs lg:text-sm text-white">
                        {massScreeningResults.slice(0, 5).map((result, index) => (
                          <div key={index} className="grid grid-cols-[minmax(0,_2fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_2fr)_minmax(0,_1fr)] gap-2 lg:gap-4 px-2 lg:px-4 py-2 lg:py-3 items-center border-b border-indigo-800/50 last:border-b-0 min-w-[480px]">
                            <div className="truncate">{result.name}</div>
                            {result.matches > 0 ? (
                              <div className="flex items-center gap-1.5 text-yellow-400 text-xs font-semibold">
                                <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.21 3.031-1.742 3.031H4.42c-1.532 0-2.492-1.697-1.742-3.031l5.58-9.92zM10 13a1 1 0 110-2 1 1 0 010 2zm-1-4a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" /></svg>
                                <span>{t.reviewNeeded}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 text-emerald-400 text-xs">
                                <svg className="w-3 h-3 lg:w-4 lg:h-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                                <span>{t.completed}</span>
                              </div>
                            )}
                            <div className={result.matches > 0 ? 'text-yellow-400 font-bold text-center' : 'text-center'}>{result.matches}</div>
                            <div className="font-mono text-xs truncate">{result.verificationId}</div>
                            <div className="flex justify-end gap-2">
                              <button className="text-red-400 hover:text-red-300 transition-colors" title={t.delete}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 lg:h-5 lg:w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V8.414a1 1 0 00-.293-.707l-4-4A1 1 0 0011.586 3H4zm4 8a1 1 0 100 2h4a1 1 0 100-2H8z" clipRule="evenodd" /></svg>
                              </button>
                              <button className="text-blue-400 hover:text-blue-300 transition-colors" title={t.view}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 lg:h-5 lg:w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a5 5 0 00-5 5v1.586l-1.707 1.707A1 1 0 004 11.586V13a1 1 0 001 1h10a1 1 0 001-1v-1.586a1 1 0 00-.293-.707L15 8.586V7a5 5 0 00-5-5zm0 12a1 1 0 100-2 1 1 0 000 2z" /></svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Swipe hint and progress indicator - Only visible on mobile, now at the end of the section */}
          <div className="md:hidden mt-8 text-center">
            <div className="bg-blue-900/20 backdrop-blur-sm text-blue-200 text-xs px-3 py-1 rounded-full border border-blue-800/30 mb-3 inline-block">
              Swipe to explore features
            </div>
            {/* Progress dots */}
            <div className="flex justify-center gap-2">
              {tabOrder.map((tab, index) => (
                <div
                  key={tab}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    activeTab === tab 
                      ? 'bg-blue-400 scale-125' 
                      : 'bg-blue-600/30'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl font-bold mb-6 text-gray-900">{t.featuresTitle}</h2>
            <p className="text-lg text-gray-600 mb-8">
              {t.featuresSubtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10 max-w-5xl mx-auto">
            {/* Smart Matching Algorithm */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-purple-100">
                <span className="text-2xl">🎯</span>
              </div>
              <div>
                <div className="font-bold text-lg text-gray-900 mb-1">{t.smartMatching}</div>
                <div className="text-gray-600 text-base">{t.smartMatchingDesc}</div>
              </div>
            </div>
            {/* Risk Classification System */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-blue-100">
                <span className="text-2xl">📊</span>
              </div>
              <div>
                <div className="font-bold text-lg text-gray-900 mb-1">{t.riskClassification}</div>
                <div className="text-gray-600 text-base">{t.riskClassificationDesc}</div>
              </div>
            </div>
            {/* Case Management */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-yellow-100">
                <span className="text-2xl">🗂️</span>
              </div>
              <div>
                <div className="font-bold text-lg text-gray-900 mb-1">{t.caseManagement}</div>
                <div className="text-gray-600 text-base">{t.caseManagementDesc}</div>
              </div>
            </div>
            {/* Continuous Monitoring */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-cyan-100">
                <span className="text-2xl">🔄</span>
              </div>
              <div>
                <div className="font-bold text-lg text-gray-900 mb-1">{t.continuousMonitoring}</div>
                <div className="text-gray-600 text-base">{t.continuousMonitoringDesc}</div>
              </div>
            </div>
            {/* Mass Screening */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-indigo-100">
                <span className="text-2xl">📝</span>
              </div>
              <div>
                <div className="font-bold text-lg text-gray-900 mb-1">{t.massScreening}</div>
                <div className="text-gray-600 text-base">{t.massScreeningDesc}</div>
              </div>
            </div>
            {/* API Integration */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-pink-100">
                <span className="text-2xl">🔌</span>
              </div>
              <div>
                <div className="font-bold text-lg text-gray-900 mb-1">{t.apiIntegration}</div>
                <div className="text-gray-600 text-base">{t.apiIntegrationDesc}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-gradient-to-br from-indigo-950 via-blue-900 to-indigo-900 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://placehold.co/1920x1080/11173F/11173F?text=')] opacity-30 mix-blend-overlay"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-6">{t.howItWorksTitle}</h2>
            <p className="text-xl text-blue-200">
              {t.howItWorksSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {/* Connect Step */}
            <div className="flex flex-col items-center relative group">
              <div className="mb-6 transform transition-all duration-300 group-hover:scale-105">
                <div className="w-20 h-20 relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-xl shadow-lg opacity-90" style={{backdropFilter: 'blur(8px)'}}></div>
                  <div className="relative z-10 text-white">
                    <svg className="w-12 h-12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7 1.49 0 2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5S21.88 11 20.5 11z"/>
                    </svg>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="w-8 h-8 rounded-full bg-[#1e3a8a] flex items-center justify-center text-white font-bold text-sm mr-2">1</span>
                {t.connect}
              </h3>
              <p className="text-blue-200 text-center px-2">
                {t.connectDesc}
              </p>
            </div>
            {/* Screen Step */}
            <div className="flex flex-col items-center relative group">
              <div className="mb-6 transform transition-all duration-300 group-hover:scale-105">
                <div className="w-20 h-20 relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] rounded-xl shadow-lg opacity-90" style={{backdropFilter: 'blur(8px)'}}></div>
                  <div className="relative z-10 text-white">
                    <svg className="w-12 h-12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-3-8c0 1.66 1.34 3 3 3s3-1.34 3-3-1.34-3-3-3-3 1.34-3 3z"/>
                      <path d="M12 4c-4.41 0-8 3.59-8 8h2c0-3.31 2.69-6 6-6V4z" opacity="0.5"/>
                      <path d="M12 6c-3.31 0-6 2.69-6 6h2c0-2.21 1.79-4 4-4V6z" opacity="0.7"/>
                    </svg>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="w-8 h-8 rounded-full bg-[#3b82f6] flex items-center justify-center text-white font-bold text-sm mr-2">2</span>
                {t.screen}
              </h3>
              <p className="text-blue-200 text-center px-2">
                {t.screenDesc}
              </p>
            </div>
            {/* Review Step */}
            <div className="flex flex-col items-center relative group">
              <div className="mb-6 transform transition-all duration-300 group-hover:scale-105">
                <div className="w-20 h-20 relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0891b2] to-[#0e7490] rounded-xl shadow-lg opacity-90" style={{backdropFilter: 'blur(8px)'}}></div>
                  <div className="relative z-10 text-white">
                    <svg className="w-12 h-12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
                      <path d="M6,8V10H8V12H10V10H12V8H10V6H8V8H6Z" fill="white"/>
                    </svg>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="w-8 h-8 rounded-full bg-[#0891b2] flex items-center justify-center text-white font-bold text-sm mr-2">3</span>
                {t.review}
              </h3>
              <p className="text-blue-200 text-center px-2">
                {t.reviewDesc}
              </p>
            </div>
            {/* Decide Step */}
            <div className="flex flex-col items-center relative group">
              <div className="mb-6 transform transition-all duration-300 group-hover:scale-105">
                <div className="w-20 h-20 relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#7c3aed] to-[#6d28d9] rounded-xl shadow-lg opacity-90" style={{backdropFilter: 'blur(8px)'}}></div>
                  <div className="relative z-10 text-white">
                    <svg className="w-12 h-12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2,18H22V20H2V18M6,16H18V17H6V16M6.5,3H17.5C18.33,3 19,3.67 19,4.5V15.5C19,16.33 18.33,17 17.5,17H6.5C5.67,17 5,16.33 5,15.5V4.5C5,3.67 5.67,3 6.5,3M7,5V15H17V5H7Z"/>
                      <path d="M9,7H15V9H9V7M9,11H15V13H9V11Z" fill="white"/>
                      <circle cx="8" cy="8" r="0.5" fill="#10b981"/>
                      <circle cx="8" cy="12" r="0.5" fill="#ef4444"/>
                    </svg>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="w-8 h-8 rounded-full bg-[#7c3aed] flex items-center justify-center text-white font-bold text-sm mr-2">4</span>
                {t.decide}
              </h3>
              <p className="text-blue-200 text-center px-2">
                {t.decideDesc}
              </p>
            </div>
          </div>
          
          {/* Workflow illustration */}
          <div className="mt-16 max-w-5xl mx-auto">
            <div className="mt-12 bg-indigo-950/50 rounded-xl p-6 border border-indigo-900/50 backdrop-blur-sm">
              <div className="flex items-center space-x-3 mb-4">
                <svg className="w-8 h-8 text-indigo-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h4 className="text-xl font-semibold text-white">{t.beyondBasic}</h4>
              </div>
              <p className="text-blue-200">
                {t.beyondBasicDesc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800">{t.pricingPage.heroTitle}</h2>
            <p className="text-xl text-gray-600">{t.pricingPage.heroSubtitle}</p>
            {/* Billing Toggle */}
            <div className="mt-10 inline-flex items-center bg-white p-1 rounded-lg shadow-sm">
              <button 
                className={`py-2 px-4 rounded-md ${annual ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                onClick={() => setAnnual(true)}
              >
                {t.pricingPage.annualBilling}
                <span className="ml-2 bg-green-500 text-white text-xs py-1 px-2 rounded-full">{t.pricingPage.save20}</span>
              </button>
              <button 
                className={`py-2 px-4 rounded-md ${!annual ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                onClick={() => setAnnual(false)}
              >
                {t.pricingPage.monthlyBilling}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Starter Plan */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transition-transform duration-300 hover:transform hover:scale-105">
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{t.pricingPage.starterPlan.title}</h3>
                <p className="text-gray-600 mb-6">{t.pricingPage.starterPlan.subtitle}</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-800">{annual ? t.pricingPage.starterPlan.priceAnnual : t.pricingPage.starterPlan.priceMonthly}</span>
                  <span className="text-gray-600">{t.perMonth}</span>
                  {annual && (
                    <>
                      <div className="text-sm text-green-600 mt-1">{t.pricingPage.starterPlan.billedAnnually}</div>
                      <div className="text-sm text-green-600 mt-1 font-semibold">{t.pricingPage.youSavePerYear.replace('{amount}', `${t.pricingPage.starterPlan.priceMonthly[0]}${(parseInt(t.pricingPage.starterPlan.priceMonthly.replace(/[^0-9]/g, '')) - parseInt(t.pricingPage.starterPlan.priceAnnual.replace(/[^0-9]/g, ''))) * 12}`)}</div>
                    </>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {t.pricingPage.starterPlan.features.map((feature, index) => (
                    <PricingFeature key={index} text={feature} />
                  ))}
                </ul>
                <button
                  onClick={handleStartFreeTrial}
                  className="block w-full text-center py-3 px-6 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition duration-200"
                >
                  {t.startFreeTrial}
                </button>
              </div>
            </div>
            {/* Professional Plan */}
            <div className="bg-white rounded-xl shadow-xl overflow-hidden border-2 border-blue-600 transform scale-105 relative z-10">
              <div className="bg-blue-600 text-white text-center py-2 text-sm font-bold">
                {t.mostPopular}
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{t.pricingPage.professionalPlan.title}</h3>
                <p className="text-gray-600 mb-6">{t.pricingPage.professionalPlan.subtitle}</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-800">{annual ? t.pricingPage.professionalPlan.priceAnnual : t.pricingPage.professionalPlan.priceMonthly}</span>
                  <span className="text-gray-600">{t.perMonth}</span>
                  {annual && (
                    <>
                      <div className="text-sm text-green-600 mt-1">{t.pricingPage.professionalPlan.billedAnnually}</div>
                      <div className="text-sm text-green-600 mt-1 font-semibold">{t.pricingPage.youSavePerYear.replace('{amount}', `${t.pricingPage.professionalPlan.priceMonthly[0]}${(parseInt(t.pricingPage.professionalPlan.priceMonthly.replace(/[^0-9]/g, '')) - parseInt(t.pricingPage.professionalPlan.priceAnnual.replace(/[^0-9]/g, ''))) * 12}`)}</div>
                    </>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {t.pricingPage.professionalPlan.features.map((feature, index) => (
                    <PricingFeature key={index} text={feature} />
                  ))}
                </ul>
                <button
                  onClick={handleStartFreeTrial}
                  className="block w-full text-center py-3 px-6 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition duration-200"
                >
                  {t.startFreeTrial}
                </button>
              </div>
            </div>
            {/* Enterprise Plan */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transition-transform duration-300 hover:transform hover:scale-105">
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{t.pricingPage.enterprisePlan.title}</h3>
                <p className="text-gray-600 mb-6">{t.pricingPage.enterprisePlan.subtitle}</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-800">{t.pricingPage.enterprisePlan.price}</span>
                  <div className="text-sm text-gray-600 mt-1">{t.pricingPage.enterprisePlan.tailoredToNeeds}</div>
                </div>
                <ul className="space-y-3 mb-8">
                  {t.pricingPage.enterprisePlan.features.map((feature, index) => (
                    <PricingFeature key={index} text={feature} />
                  ))}
                </ul>
                <Link 
                  to="/contact" 
                  className="block w-full text-center py-3 px-6 rounded-lg bg-gray-800 hover:bg-gray-900 text-white font-medium transition duration-200"
                >
                  {t.contactSales}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Feature Comparison Table */}
      <section className="py-20 bg-gradient-to-br from-indigo-950 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 text-white">{t.pricingPage.compareFeatures.title}</h2>
            <p className="text-xl text-blue-200">{t.pricingPage.compareFeatures.subtitle}</p>
          </div>
          {/* Desktop Table */}
          <div className="hidden md:block max-w-6xl mx-auto overflow-x-auto">
            <table className="min-w-full border-collapse rounded-xl shadow-xl overflow-hidden bg-indigo-950/60">
              <thead>
                <tr className="bg-indigo-900/80">
                  <th className="p-4 text-left text-blue-200 font-medium border-b border-indigo-800/50 w-1/3">{t.pricingPage.compareFeatures.features}</th>
                  <th className="p-4 text-center text-blue-200 font-medium border-b border-indigo-800/50">{t.pricingPage.starterPlan.title}</th>
                  <th className="p-4 text-center text-blue-300 font-medium border-b border-indigo-800/50">{t.pricingPage.professionalPlan.title}</th>
                  <th className="p-4 text-center text-blue-200 font-medium border-b border-indigo-800/50">{t.pricingPage.enterprisePlan.title}</th>
                </tr>
              </thead>
              <tbody>
                <FeatureRow 
                  feature={t.pricingPage.compareFeatures.monthlyScreenings}
                  starter={t.pricingPage.compareFeatures.starterMonthlyScreenings}
                  pro={t.pricingPage.compareFeatures.professionalMonthlyScreenings}
                  enterprise={t.pricingPage.compareFeatures.enterpriseMonthlyScreenings}
                />
                <FeatureRow 
                  feature={t.pricingPage.compareFeatures.euSanctionsLists}
                  starter={<span className="text-green-400">✓</span>} 
                  pro={<span className="text-green-400">✓</span>} 
                  enterprise={<span className="text-green-400">✓</span>} 
                />
                <FeatureRow 
                  feature={t.pricingPage.compareFeatures.unSanctionsLists}
                  starter={<span className="text-green-400">✓</span>} 
                  pro={<span className="text-green-400">✓</span>} 
                  enterprise={<span className="text-green-400">✓</span>} 
                />
                <FeatureRow 
                  feature={t.pricingPage.compareFeatures.ofacSanctionsLists}
                  starter={<span className="text-green-400">✓</span>} 
                  pro={<span className="text-green-400">✓</span>} 
                  enterprise={<span className="text-green-400">✓</span>} 
                />
                <FeatureRow 
                  feature={t.pricingPage.compareFeatures.aiPoweredMatching}
                  starter={<span className="text-green-400">✓ {t.pricingPage.compareFeatures.basic}</span>} 
                  pro={<span className="text-green-400">✓ {t.pricingPage.compareFeatures.advanced}</span>} 
                  enterprise={<span className="text-green-400">✓ {t.pricingPage.compareFeatures.premium}</span>} 
                />
                <FeatureRow 
                  feature={t.pricingPage.compareFeatures.caseManagement}
                  starter={<span className="text-green-400">✓</span>} 
                  pro={<span className="text-green-400">✓</span>} 
                  enterprise={<span className="text-green-400">✓</span>} 
                />
                <FeatureRow 
                  feature={t.pricingPage.compareFeatures.bulkUpload}
                  starter={<span className="text-red-400">✗</span>} 
                  pro={<span className="text-green-400">✓</span>} 
                  enterprise={<span className="text-green-400">✓</span>} 
                />
                <FeatureRow 
                  feature={t.pricingPage.compareFeatures.activeMonitoring}
                  starter={<span className="text-red-400">✗</span>} 
                  pro={<span className="text-green-400">✓</span>} 
                  enterprise={<span className="text-green-400">✓</span>} 
                />
                <FeatureRow 
                  feature={t.pricingPage.compareFeatures.support}
                  starter={<span className="text-green-400">{t.pricingPage.compareFeatures.email}</span>} 
                  pro={<span className="text-green-400">{t.pricingPage.compareFeatures.emailAndChat}</span>} 
                  enterprise={<span className="text-green-400">{t.pricingPage.compareFeatures.priority247}</span>} 
                />
              </tbody>
            </table>
          </div>
          {/* Mobile Stacked Cards */}
          <div className="block md:hidden space-y-6">
            {/* Starter Card */}
            <div className="bg-indigo-950/60 rounded-xl shadow-xl p-4">
              <h3 className="text-xl font-bold text-blue-200 mb-2">{t.pricingPage.starterPlan.title}</h3>
              <ul className="divide-y divide-indigo-900/50">
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.monthlyScreenings}</span><span className="font-bold">{t.pricingPage.compareFeatures.starterMonthlyScreenings}</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.euSanctionsLists}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.unSanctionsLists}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.ofacSanctionsLists}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.aiPoweredMatching}</span><span className="text-green-400">✓ {t.pricingPage.compareFeatures.basic}</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.caseManagement}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.bulkUpload}</span><span className="text-red-400">✗</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.activeMonitoring}</span><span className="text-red-400">✗</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.support}</span><span className="text-green-400">{t.pricingPage.compareFeatures.email}</span></li>
              </ul>
            </div>
            {/* Professional Card */}
            <div className="bg-indigo-950/60 rounded-xl shadow-xl p-4">
              <h3 className="text-xl font-bold text-blue-200 mb-2">{t.pricingPage.professionalPlan.title}</h3>
              <ul className="divide-y divide-indigo-900/50">
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.monthlyScreenings}</span><span className="font-bold">{t.pricingPage.compareFeatures.professionalMonthlyScreenings}</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.euSanctionsLists}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.unSanctionsLists}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.ofacSanctionsLists}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.aiPoweredMatching}</span><span className="text-green-400">✓ {t.pricingPage.compareFeatures.advanced}</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.caseManagement}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.bulkUpload}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.activeMonitoring}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.support}</span><span className="text-green-400">{t.pricingPage.compareFeatures.emailAndChat}</span></li>
              </ul>
            </div>
            {/* Enterprise Card */}
            <div className="bg-indigo-950/60 rounded-xl shadow-xl p-4">
              <h3 className="text-xl font-bold text-blue-200 mb-2">{t.pricingPage.enterprisePlan.title}</h3>
              <ul className="divide-y divide-indigo-900/50">
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.monthlyScreenings}</span><span className="font-bold">{t.pricingPage.compareFeatures.enterpriseMonthlyScreenings}</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.euSanctionsLists}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.unSanctionsLists}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.ofacSanctionsLists}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.aiPoweredMatching}</span><span className="text-green-400">✓ {t.pricingPage.compareFeatures.premium}</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.caseManagement}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.bulkUpload}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.activeMonitoring}</span><span className="text-green-400">✓</span></li>
                <li className="py-2 flex justify-between"><span>{t.pricingPage.compareFeatures.support}</span><span className="text-green-400">{t.pricingPage.compareFeatures.priority247}</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">{t.contactPage?.faqTitle || 'Frequently Asked Questions'}</h2>
            <p className="text-xl text-gray-600">
              {t.contactPage?.faqSubtitle || 'Quick answers to common questions'}
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {faq.map((item, idx) => (
              <FAQItem key={idx} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-950 via-blue-900 to-indigo-900 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://placehold.co/1920x1080/11173F/11173F?text=')] opacity-30 mix-blend-overlay"></div>
        
        <div className="absolute -left-40 top-20">
          <div className="w-80 h-80 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full blur-3xl opacity-20"></div>
        </div>
        <div className="absolute -right-40 bottom-20">
          <div className="w-80 h-80 bg-gradient-to-tr from-indigo-400 to-pink-500 rounded-full blur-3xl opacity-20"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <h2 className="text-4xl font-bold mb-4">{t.newsletterTitle}</h2>
          <p className="text-xl text-blue-200 max-w-2xl mx-auto mb-8">
            {t.newsletterSubtitle}
          </p>
          <form onSubmit={handleNewsletterSubscribe} className="max-w-lg mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder={t.newsletterPlaceholder}
                className="flex-grow bg-white/10 border border-white/20 text-white placeholder-blue-200 rounded-md py-3 px-5 focus:outline-none focus:border-white transition"
                required
              />
              <button
                type="submit"
                disabled={newsletterLoading}
                className="bg-white text-indigo-900 hover:bg-indigo-50 font-semibold py-3 px-8 rounded-md transition duration-200 whitespace-nowrap disabled:opacity-50"
              >
                {newsletterLoading ? t.loading : t.newsletterButton}
              </button>
            </div>
            {newsletterStatus === 'success' && (
              <p className="text-emerald-400 mt-4">{t.newsletterSuccess}</p>
            )}
            {newsletterStatus === 'error' && (
              <p className="text-red-400 mt-4">{t.newsletterError}</p>
            )}
          </form>
        </div>

        {/* Footer */}
        <footer className="text-white mt-16">
          <div className="container mx-auto px-6">
            {/* Add your footer content here */}
          </div>
        </footer>
      </section>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
      <div className="mb-5">{icon}</div>
      <h3 className="text-xl font-semibold mb-3 text-gray-900">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

// Add these helper components for pricing and feature comparison
const PricingFeature = ({ text }) => {
  return (
    <li className="flex items-center">
      <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
      <span className="text-gray-700">{text}</span>
    </li>
  );
};

const FeatureRow = ({ feature, starter, pro, enterprise }) => {
  return (
    <tr className="border-b border-indigo-900/50 hover:bg-indigo-900/40 transition-colors">
      <td className="p-4 text-white font-medium">{feature}</td>
      <td className="p-4 text-center text-blue-200">{starter}</td>
      <td className="p-4 text-center text-blue-100 bg-blue-900/30">{pro}</td>
      <td className="p-4 text-center text-blue-200">{enterprise}</td>
    </tr>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <button 
        className="w-full flex justify-between items-center p-6 text-left focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-lg font-medium text-gray-800">{question}</span>
        <svg 
          className={`w-5 h-5 text-gray-500 transform ${isOpen ? 'rotate-180' : ''} transition-transform duration-200`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 pb-6">
          <div className="border-t border-gray-200 pt-4">
            <p className="text-gray-600">{answer}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage; 